import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyIconButton,
} from '@equip.health/ui';

import { MaiFeedbackRating } from '~/lib/types/mai/MaiFeedbackRating';

import { MAI_FEEDBACK_LABELS } from './maiFeedback.constants';

interface MaiChatFeedbackBarProps {
  messageId: string;
  serverMessageId?: string;
  rating?: MaiFeedbackRating;
  isSubmitting?: boolean;
  onRate: (serverMessageId: string, rating: MaiFeedbackRating) => void;
  onRegenerate: (messageId: string) => void;
}

interface MaiChatFeedbackAction {
  ariaLabel: string;
  color?: string;
  icon: FAIconName;
  isActive: boolean;
  isDisabled: boolean;
  key: string;
  onClick: () => void;
}

const IDLE_COLOR = 'neutral.secondary';
const ACTIVE_COLOR = 'primary.100';
const thumbColor = (isActive: boolean) =>
  isActive ? ACTIVE_COLOR : IDLE_COLOR;

const MaiChatFeedbackBar = ({
  messageId,
  serverMessageId,
  rating,
  isSubmitting = false,
  onRate,
  onRegenerate,
}: MaiChatFeedbackBarProps) => {
  const canRate = Boolean(serverMessageId) && !isSubmitting;
  const isUp = rating === 'up';
  const isDown = rating === 'down';

  const rate = (next: MaiFeedbackRating) => () => {
    if (serverMessageId) onRate(serverMessageId, next);
  };

  const actions: MaiChatFeedbackAction[] = [
    {
      key: 'helpful',
      ariaLabel: isUp
        ? MAI_FEEDBACK_LABELS.helpful.active
        : MAI_FEEDBACK_LABELS.helpful.idle,
      color: thumbColor(isUp),
      icon: FAIconName.thumbsUpRegular,
      isActive: isUp,
      isDisabled: !canRate,
      onClick: rate('up'),
    },
    {
      key: 'not-helpful',
      ariaLabel: isDown
        ? MAI_FEEDBACK_LABELS.notHelpful.active
        : MAI_FEEDBACK_LABELS.notHelpful.idle,
      color: thumbColor(isDown),
      icon: FAIconName.thumbsDownRegular,
      isActive: isDown,
      isDisabled: !canRate,
      onClick: rate('down'),
    },
    {
      key: 'regenerate',
      ariaLabel: 'Regenerate response',
      icon: FAIconName.arrowsRotateRegular,
      isActive: false,
      isDisabled: false,
      onClick: () => onRegenerate(messageId),
    },
  ];

  return (
    <ChakraBox data-testid="mai-chat-ai-message__feedback">
      <ChakraFlex alignItems="center" gap="2px" marginTop="2px">
        {actions.map((action) => (
          <FireflyIconButton
            key={action.key}
            id={`${messageId}__${action.key}`}
            ariaLabel={action.ariaLabel}
            color={action.color}
            icon={action.icon}
            isActive={action.isActive}
            isDisabled={action.isDisabled}
            onClick={action.onClick}
            size="small"
            variant="tertiary"
          />
        ))}
      </ChakraFlex>
    </ChakraBox>
  );
};

export default MaiChatFeedbackBar;
